import { yupResolver } from '@hookform/resolvers/yup'
import { isAfter, isWithinInterval } from '@if/core/date'
import {
  EApplicantType,
  EBeneficiaryType,
  ECreditApplicationStatus,
  EFinancingProgram,
  ELoanPurpose,
  EMerchantType,
  EPaymentPlan,
  EProvince,
  ERelationToApplicant,
} from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import { ControlledNumberField, ControlledSelect, ControlledTextField } from '@if/mui'
import InfoIcon from '@mui/icons-material/Info'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import StarIcon from '@mui/icons-material/Star'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import CreditConsentDialog from '@src/components/CreditConsentDialog'
import PageSpinner from '@src/components/PageSpinner'
import ServerErrorsAlert from '@src/components/ServerErrorsAlert'
import TabPanel from '@src/components/TabPanel'
import ConflictDialog from '@src/containers/App/components/ConflictDialog'
import { useBeneficiaryTypes } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { transformAddressFromApi, transformApplicantJobFromApi } from '@src/data/api/credit-api/credit-transform'
import { useMerchantById, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import useFeatureFlags from '@src/hooks/config-hooks'
import { reportErrorToConsole } from '@src/services/error-logger'
import { Address } from '@src/types/AddressSchema'
import { Conflict } from '@src/types/Conflict'
import { FinancingProgramConfigs, useDevTools } from '@src/types/Constants'
import {
  ApplicantDto,
  CreditApplication,
  DraftApplicantSchema,
  DraftCreditApplicationDto,
  buildDraftCreditApplicationSchema,
  buildFullCreditApplicationSchema,
} from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { AxiosError } from 'axios'
import { ChangeEventHandler, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BusinessError, validateCreditApplicationCanBeSubmittedForReview } from '../../../data/credit-app-validations'
import ApplicantParameters from './Applicant/ApplicantComponent'
import BeneficiaryForm from './Applicant/components/BeneficiaryForm'
import MerchantSelector from './Applicant/components/MerchantSelector'
import GetCreditContentConsentText from './creditApplicationForm-selectors'
import TestCaseDialog from './ListTestCaseDialog'

interface Props {
  merchant: Merchant | null
  creditApplication: DraftCreditApplicationDto
  onSave: (data: Partial<CreditApplication>) => void
  isPending: boolean
  isError: boolean
  serverErrors: AxiosError | null
  financingProgramId: EFinancingProgram
  lockApplicantIncomeAndExpenses?: boolean
  lockCoApplicantIncomeAndExpenses?: boolean
  isPrequalComplete: boolean
  isApplicantSinNeeded: boolean | null
  isCoapplicantSinNeeded: boolean | null
}

const enum DialogComponentRender {
  prequalify,
  qualify,
  submit,
}

const CreditApplicationForm = ({
  merchant,
  creditApplication,
  onSave,
  isPending,
  isError,
  serverErrors,
  financingProgramId,
  lockApplicantIncomeAndExpenses,
  lockCoApplicantIncomeAndExpenses,
  isPrequalComplete,
  isApplicantSinNeeded,
  isCoapplicantSinNeeded,
}: Props) => {
  const [draftMod, setDraftMod] = useState(
    !creditApplication || creditApplication.status === ECreditApplicationStatus.Draft,
  )

  // Fetch merchant-specific financing config from backend (only for Personal Loan)
  const shouldFetchMerchantConfig = financingProgramId === EFinancingProgram.Personal && !!merchant?.id
  const [merchantFinancingConfig, isLoadingConfig] = useMerchantFinancingConfig(
    shouldFetchMerchantConfig ? merchant?.id : null,
    financingProgramId,
  )

  const isMedicardServiceCategory =
    merchant?.serviceCategory === ELoanPurpose.Medical ||
    merchant?.serviceCategory === ELoanPurpose.Dentistry ||
    merchant?.serviceCategory === ELoanPurpose.Aesthetics

  // Determine category-based max loan amount (50k for Medicard, 40k otherwise)
  const defaultConfig = FinancingProgramConfigs[financingProgramId]
  const categoryBasedMax =
    financingProgramId === EFinancingProgram.Personal && isMedicardServiceCategory
      ? defaultConfig.maxLoanAmountMedicard
      : defaultConfig.maxLoanAmount

  // Use the lower of category-based max and merchant config (if present)
  const maxLoanAmount = Math.min(categoryBasedMax, merchantFinancingConfig?.maximumLoanAmount ?? categoryBasedMax)
  const minLoanAmount = merchantFinancingConfig?.minimumLoanAmount ?? defaultConfig.minLoanAmount

  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    trigger,
    setValue,
    reset,
    formState: { errors },
  } = useForm<DraftCreditApplicationDto>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: { ...creditApplication, financingProgramId, merchantPaymentPlanId: creditApplication.merchantPaymentPlanId ?? EPaymentPlan.RegularDailyInterests },
    context: { maxLoanValue: maxLoanAmount, minLoanValue: minLoanAmount },
    resolver: draftMod
      ? yupResolver(buildDraftCreditApplicationSchema())
      : yupResolver(buildFullCreditApplicationSchema()),
  })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [hasPrequalifyBeenPressed, setHasPrequalifyBeenPressed] = useState(false)
  const alertRef = useRef<HTMLDivElement>(null)

  const addCoappParam = searchParams.get('addCoApp')

  const programConfig = programConfigByFinancingProgramId(financingProgramId)
  const addCoApp = Boolean(addCoappParam) && programConfig.supportsCoApplicant
  const isBanner = merchant?.merchantTypeId === EMerchantType.Banner
  const [merchantsList] = useMerchantByParentId(merchant?.id ?? '')
  const watchedMerchantId = watch('merchantId')
  const [bannerSelectedMerchant] = useMerchantById(isBanner ? watchedMerchantId : '')
  const selectedMerchant = isBanner ? bannerSelectedMerchant : merchant
  const [hasCoapplicantChecked, setCoApplicantChecked] = useState<boolean>(!!creditApplication.coApplicant || addCoApp)
  const [openConsentDialog, setOpenConsentDialog] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const [globalErrors, setGlobalErrors] = useState<BusinessError[]>([])
  const [dialogRender, setDialagRender] = useState<DialogComponentRender>(DialogComponentRender.prequalify)
  const [testOpenDialog, setTestOpenDialog] = useState<boolean>(false)
  const [conflict, setConflict] = useState<Conflict | null>(null)
  const [isFormValid, setIsFormValid] = useState(true)

  const activePaymentPlans = useMemo(() => {
    if (!selectedMerchant?.paymentPlans) return []
    const currentDate = new Date()
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)
    const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))

    return selectedMerchant.paymentPlans.filter((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      return (
        isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn }) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)
      )
    })
  }, [selectedMerchant])

  const hasActivePaymentPlans = activePaymentPlans.length > 0
  const { flags } = useFeatureFlags()
  const useBuyDownSelector = flags.UseBuyDownSelectorMerchantDB
  const hasPlanPromoRules = (selectedMerchant?.planPromoRules?.length ?? 0) > 0

  // Find the payment plan to preselect based on planPromoRules (highest star count)
  const planPromoRuleDefaultPlanId = useMemo(() => {
    if (!useBuyDownSelector) return null
    if (!selectedMerchant?.planPromoRules?.length || !activePaymentPlans.length) return null
    const starRules = [...selectedMerchant.planPromoRules]
      .filter(r => /^\*+$/.test(r.planName))
      .sort((a, b) => b.planName.length - a.planName.length)
    if (!starRules.length) return null
    const bestRule = starRules[0]
    const matchingPlan = activePaymentPlans.find(p =>
      p.promoCode === bestRule.defaultPromoCode
    )
    return matchingPlan?.id ?? null
  }, [useBuyDownSelector, selectedMerchant?.planPromoRules, activePaymentPlans])

  const promoCodeMinStars = useMemo(() => {
    const map = new Map<string, number>()
    if (!useBuyDownSelector || !selectedMerchant?.planPromoRules) return map
    for (const rule of selectedMerchant.planPromoRules) {
      if (!/^\*+$/.test(rule.planName)) continue
      const stars = rule.planName.length
      const codes = [rule.defaultPromoCode, ...(rule.selectablePromoCodes ?? [])]
      for (const code of codes) {
        const current = map.get(code)
        if (current === undefined || stars < current) map.set(code, stars)
      }
    }
    return map
  }, [useBuyDownSelector, selectedMerchant?.planPromoRules])

  const paymentPlanOptions = useMemo(
    () => [
      <MenuItem key="default" value={EPaymentPlan.RegularDailyInterests}>
        {t('common.regularPlan')}
      </MenuItem>,
      ...activePaymentPlans.map((plan) => {
        const promoCode = plan.promoCode.replace('PLAN', '').trim()
        const minStars = promoCodeMinStars.get(plan.promoCode) ?? promoCodeMinStars.get(promoCode)
        return (
          <MenuItem key={plan.id} value={plan.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {promoCode}
              {minStars != null && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                  ({minStars}
                  <StarIcon sx={{ fontSize: 14 }} />{minStars < 5 && '+'})
                </Box>
              )}
            </Box>
          </MenuItem>
        )
      }),
    ],
    [activePaymentPlans, t, promoCodeMinStars],
  )

  const loanPurposeId = watch('loanPurposeId') as ELoanPurpose
  const beneficiaryTypeId = Number(watch('beneficiaryTypeId')) as EBeneficiaryType
  const isVeterinaryLoanPurpose = ELoanPurpose.Veterinary === loanPurposeId
  const isGoodAndServiceLoanPurpose = ELoanPurpose.GoodsAndServices === loanPurposeId
  const isHomeSolutionsLoanPurpose = ELoanPurpose.HomeSolutions === loanPurposeId
  const disableOtherName = beneficiaryTypeId !== EBeneficiaryType.Other
  const showTabIndex = searchParams.get('showTabIndex')
  const tabIndex = showTabIndex && programConfig.supportsCoApplicant ? Number(showTabIndex) : 0
  const editDisabled = creditApplication?.editLocked
  const stateIso = watch('applicant.currentAddress.stateIso')
  const noConsentNeeded: boolean =
    creditApplication.financingProgramId === EFinancingProgram.Products && stateIso !== EProvince.Quebec
  const isQualify =
    (!programConfig.useSoftHit && programConfig.useHardHit) ||
    programConfig.useAlwaysSubmit ||
    creditApplication.consentHardHit
  const isEditingApplication = !!creditApplication.id
  useEffect(() => {
    if (merchant && !isBanner) {
      setValue('merchantId', merchant.id, { shouldValidate: true })
      setValue('loanPurposeId', merchant?.serviceCategory ?? '', { shouldValidate: true })
    }
    if (isVeterinaryLoanPurpose) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Other, { shouldValidate: true })
      setValue('otherBeneficiaryFirstName', '', { shouldValidate: true })
    }
    if (beneficiaryTypeId === EBeneficiaryType.Applicant || beneficiaryTypeId === EBeneficiaryType.Coapplicant) {
      setValue('otherBeneficiaryFirstName', '', { shouldValidate: true })
      setValue('otherBeneficiaryLastName', '', { shouldValidate: true })
    }
    if (isGoodAndServiceLoanPurpose || isHomeSolutionsLoanPurpose) {
      setValue('beneficiaryTypeId', EBeneficiaryType.Applicant, { shouldValidate: true })
    }
    if (loanPurposeId !== ELoanPurpose.Dentistry && watch('isInvisalign')) {
      setValue('isInvisalign', false)
    }

    if (financingProgramId === EFinancingProgram.Products) {
      setValue('requestedLoanAmount', 0)
    }
    if (serverErrors) {
      if (serverErrors.response?.status === 409) {
        setConflict(serverErrors.response?.data as Conflict)
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }, [
    merchant,
    isBanner,
    setValue,
    isVeterinaryLoanPurpose,
    serverErrors,
    beneficiaryTypeId,
    isGoodAndServiceLoanPurpose,
    financingProgramId,
    isHomeSolutionsLoanPurpose,
    loanPurposeId,
    watch,
  ])

  useEffect(() => {
    if (planPromoRuleDefaultPlanId && !creditApplication.merchantPaymentPlanId) {
      setValue('merchantPaymentPlanId', planPromoRuleDefaultPlanId, { shouldValidate: true })
      const plan = activePaymentPlans.find(p => p.id === planPromoRuleDefaultPlanId)
      if (plan) setValue('prequalPromoCode', plan.promoCode)
    }
  }, [planPromoRuleDefaultPlanId, creditApplication.merchantPaymentPlanId, setValue, activePaymentPlans])

  const beneficiaryTypes = useBeneficiaryTypes(loanPurposeId)

  const handleToggleCoapplicant = useCallback(
    (checked: boolean) => {
      if (checked) {
        setValue('coApplicant', {
          ...DraftApplicantSchema().getDefault(),
        } as ApplicantDto)
        setValue('coApplicant.isPrimaryApplicant', false)
      } else {
        setValue('coApplicant', null)
      }
      setCoApplicantChecked(checked)
    },
    [setValue],
  )

  useEffect(() => {
    if (addCoApp) handleToggleCoapplicant(true)
  }, [addCoApp, handleToggleCoapplicant])

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      const { location } = window
      const updateSearch = new URLSearchParams(location.search)
      updateSearch.set('showTabIndex', encodeURIComponent(newValue))
      const newUrl = `${location.pathname}?${updateSearch.toString()}`
      navigate(newUrl, { replace: true })
    },
    [navigate],
  )

  const submit = useCallback(
    (data: CreditApplication) => {
      onSave(data)
    },
    [onSave],
  )

  const handlePrequalify = useCallback(
    async (data: CreditApplication) => {
      const isValid = await trigger()
      if (isValid) {
        const cErrors = validateCreditApplicationCanBeSubmittedForReview(data, selectedMerchant)
        setGlobalErrors(cErrors)
        if (cErrors.length <= 0) {
          data.status = ECreditApplicationStatus.Active
          data.consentSoftHit = true
          data.consentHardHit =
            dialogRender === DialogComponentRender.qualify || dialogRender === DialogComponentRender.submit
          onSave(data)
        }
      }
    },
    [trigger, dialogRender, onSave, selectedMerchant],
  )

  const handleCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setConsentChecked(event.target.checked)
  }, [])

  const handleConfirm = useCallback(
    async (data: CreditApplication) => {
      if (consentChecked) {
        setOpenConsentDialog(false)
        await handlePrequalify(data)
      }
    },
    [consentChecked, handlePrequalify],
  )

  const closeDialogs = useCallback(() => {
    setTestOpenDialog(false)
  }, [setTestOpenDialog])

  const getDialogContent = useMemo(() => {
    if (noConsentNeeded) return <Typography>{t('common.submitConfirmation')}</Typography>

    switch (dialogRender) {
      case DialogComponentRender.prequalify:
        return <Typography>{t('submission.creditAgreementMessage')}</Typography>
      case DialogComponentRender.qualify:
        return <GetCreditContentConsentText financingProgram={financingProgramId} />
      default:
        return <Typography>{t('common.submitConfirmation')}</Typography>
    }
  }, [dialogRender, financingProgramId, noConsentNeeded, t])

  const getDialogConfirmationButtonText = useMemo(() => {
    switch (dialogRender) {
      case DialogComponentRender.prequalify:
        return t('submission.submitPrequal')
      case DialogComponentRender.qualify:
        return t('submission.submit')
      default:
        return <Typography>{t('common.submit')}</Typography>
    }
  }, [dialogRender, t])

  const onTestSelected = useCallback(
    (applicant: ApplicantDto) => {
      const transformedAddress = transformAddressFromApi(applicant.currentAddress as Address)
      applicant.currentAddress = { ...transformedAddress, years: transformedAddress.years ?? 0 }
      applicant.currentJobs = applicant.currentJobs.map(transformApplicantJobFromApi)
      applicant.previousJobs = applicant.previousJobs.map(transformApplicantJobFromApi)

      if (tabIndex === 0) {
        applicant.isPrimaryApplicant = true
        reset({ ...getValues(), applicant })
      } else {
        applicant.isPrimaryApplicant = false
        applicant.relationWithApplicant = ERelationToApplicant.CommonLaw
        reset({ ...getValues(), coApplicant: applicant })
      }
      closeDialogs()
    },
    [tabIndex, closeDialogs, reset, getValues],
  )

  const onMerchantChange = (_event: unknown, newMerchant: Merchant | null) => {
    if (newMerchant) {
      setValue('merchantId', newMerchant.id, { shouldValidate: true })
      setValue('loanPurposeId', newMerchant.serviceCategory ?? '', { shouldValidate: true })
      setValue('merchantPaymentPlanId', EPaymentPlan.RegularDailyInterests, { shouldValidate: true })
      setValue('prequalPromoCode', null)
      if (newMerchant.serviceCategory === ELoanPurpose.GoodsAndServices) {
        setValue('beneficiaryTypeId', EBeneficiaryType.Applicant, { shouldValidate: true })
        setValue('otherBeneficiaryFirstName', undefined, { shouldValidate: true })
        setValue('otherBeneficiaryLastName', undefined, { shouldValidate: true })
      }
    }
  }

  const handleCloseWithoutRefreshConflictDialog = () => {
    setConflict(null)
  }
  const onSubmit = async () => {
    const isValid = await trigger()
    setIsFormValid(isValid)
    const cErrors = validateCreditApplicationCanBeSubmittedForReview(getValues() as CreditApplication, selectedMerchant)
    setGlobalErrors(cErrors)
    if (isValid && cErrors.length <= 0) {
      if (
        creditApplication.consentHardHit ||
        (!programConfig.useHardHit && !programConfig.useHardHitForSpecificProvince?.includes(stateIso!))
      ) {
        setDialagRender(DialogComponentRender.submit)
      } else {
        setDialagRender(DialogComponentRender.qualify)
      }
      setOpenConsentDialog(true)
      setDraftMod(false)
    } else if (cErrors.length > 0) {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }

  const onPrequalify = async () => {
    setHasPrequalifyBeenPressed(true)
    const isValid = await trigger()
    setIsFormValid(isValid)
    const cErrors = validateCreditApplicationCanBeSubmittedForReview(getValues() as CreditApplication, selectedMerchant)
    setGlobalErrors(cErrors)
    if (isValid && cErrors.length <= 0) {
      setOpenConsentDialog(true)
      setDraftMod(false)
    } else if (cErrors.length > 0) {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }

  return (
    <>
      <TestCaseDialog onClose={closeDialogs} open={testOpenDialog} onTestCaseSelected={onTestSelected} />
      <PageSpinner isLoading={isPending || (shouldFetchMerchantConfig && isLoadingConfig)} withBackdrop />
      <Paper>{serverErrors && <ServerErrorsAlert serverErrors={serverErrors} />}</Paper>
      {conflict && (
        <ConflictDialog
          updatedOn={conflict?.updatedOn}
          open={conflict !== null}
          onClose={handleCloseWithoutRefreshConflictDialog}
        />
      )}
      <form onSubmit={handleSubmit((data) => submit(data as CreditApplication), reportErrorToConsole)}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
              <MerchantSelector
                isBanner={isBanner}
                merchantsList={merchantsList}
                onChange={onMerchantChange}
                errors={errors}
                merchant={selectedMerchant}
              />
              <ControlledTextField
                id="merchantReferenceCodeId"
                name="merchantReferenceCode"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {`${t('applicationHeader.yourReferenceCode')} (${String(t('common.optional'))})`}
                    <Tooltip title={t('applicationHeader.tooltips.merchantReferenceCodeInfo')}>
                      <InfoIcon sx={{ fontSize: 18, ml: 0.5, cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                }
                control={control}
                type="string"
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            </Stack>
            <Box mb={2} />
            <Stack spacing={2}>
              {programConfig.supportsSinglePayment && (
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('editCreditApplication.paymentOptions')}</FormLabel>
                  <RadioGroup
                    row
                    value={watch('isSinglePayment') === true ? 'single' : 'loan'}
                    onChange={(event) => {
                      const isSingle = event.target.value === 'single'
                      setValue('isSinglePayment', isSingle)
                    }}
                    sx={{ pl: 1 }}
                  >
                    <FormControlLabel
                      value="single"
                      control={<Radio />}
                      label={t('editCreditApplication.singlePayment')}
                    />
                    <FormControlLabel value="loan" control={<Radio />} label={t('editCreditApplication.financing')} />
                  </RadioGroup>
                </FormControl>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 1 }}>
                {programConfig.loanPurposeIdIsRequired && (
                  <ControlledTextField
                    name="loanPurposeId"
                    control={control}
                    id="loanPurposeId"
                    label={t('editCreditApplication.loanPurpose')}
                    fullWidth
                    disabled
                  />
                )}
                {programConfig.requestedLoanAmountIsRequired && (
                  <ControlledNumberField
                    id="amountRequested"
                    name="requestedLoanAmount"
                    control={control}
                    label={t('editCreditApplication.amountRequested')}
                    fullWidth
                    isMoney
                  />
                )}
                {hasActivePaymentPlans && (useBuyDownSelector || !hasPlanPromoRules) && (
                  <ControlledSelect
                    control={control}
                    name="merchantPaymentPlanId"
                    options={paymentPlanOptions}
                    label={t('worksheet.paymentPlan')}
                    fullWidth
                    disabled={editDisabled || isPrequalComplete}
                    disableEmptyOption
                    onChange={(planId) => {
                      const plan = activePaymentPlans.find(p => p.id === planId)
                      setValue('prequalPromoCode', plan?.promoCode ?? null)
                    }}
                  />
                )}
              </Stack>
              {loanPurposeId === ELoanPurpose.Dentistry && (
                <FormControl component="fieldset" disabled={editDisabled}>
                  <FormLabel component="legend">{t('editCreditApplication.invisalign.question')}</FormLabel>
                  <RadioGroup
                    row
                    value={watch('isInvisalign') === true ? 'yes' : 'no'}
                    onChange={(event) => setValue('isInvisalign', event.target.value === 'yes')}
                    sx={{ pl: 1 }}
                  >
                    <FormControlLabel value="no" control={<Radio />} label={t('common.no')} disabled={editDisabled} />
                    <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} disabled={editDisabled} />
                  </RadioGroup>
                </FormControl>
              )}
              {programConfig.beneficiaryIsRequired && !(isGoodAndServiceLoanPurpose || isHomeSolutionsLoanPurpose) && (
                <BeneficiaryForm
                  beneficiaryTypes={beneficiaryTypes}
                  isVeterinaryLoanPurpose={isVeterinaryLoanPurpose}
                  disableOtherName={disableOtherName}
                  formControl={control}
                />
              )}
            </Stack>
          </CardContent>
          {hasPrequalifyBeenPressed && !isFormValid && (
            <CardContent>
              <Alert severity="error">
                <AlertTitle>{t('common.error')}</AlertTitle>
                <Typography sx={{ whiteSpace: 'pre-line' }}>{t('common.errors.fixProvidedErrors')}</Typography>
              </Alert>
            </CardContent>
          )}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs variant="fullWidth" value={tabIndex} onChange={handleTabChange}>
              <Tab
                id="applicantTab"
                icon={errors.applicant ? <PriorityHighIcon color="error" /> : ''}
                iconPosition="end"
                label={t('common.applicant')}
                disabled={isPrequalComplete}
              />
              {programConfig.supportsCoApplicant && (
                <Tab
                  id="coApplicantTab"
                  icon={errors.coApplicant ? <PriorityHighIcon color="error" /> : ''}
                  iconPosition="end"
                  label={t('common.coApplicant')}
                />
              )}
            </Tabs>
          </Box>
          <input type="hidden" value=" " {...register('id')} />
          <input type="hidden" value=" " {...register('merchantId')} />
          <CardContent>
            <TabPanel value={tabIndex} index={0}>
              {useDevTools && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button type="button" variant="contained" color="secondary" onClick={() => setTestOpenDialog(true)}>
                    {t('common.testCase')}
                  </Button>
                </Box>
              )}
              <ApplicantParameters
                watch={watch}
                control={control}
                setValue={setValue}
                errors={errors.applicant}
                applicantType={EApplicantType.Applicant}
                formControl={control}
                editDisabled={editDisabled || (lockApplicantIncomeAndExpenses ?? false) || isPrequalComplete}
                financingProgramId={financingProgramId}
                hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
                isSinNeeded={isApplicantSinNeeded}
              />
            </TabPanel>
            {programConfig.supportsCoApplicant && (
              <TabPanel value={tabIndex} index={1}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="toggleCoapplicant"
                        checked={hasCoapplicantChecked}
                        onChange={(e) => handleToggleCoapplicant(e.target.checked)}
                      />
                    }
                    label={t('common.coApplicant')}
                  />
                </div>
                {hasCoapplicantChecked && (
                  <>
                    {useDevTools && (
                      <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
                        <Button
                          type="button"
                          variant="contained"
                          color="secondary"
                          onClick={() => setTestOpenDialog(true)}
                        >
                          {t('common.testCase')}
                        </Button>
                      </Box>
                    )}
                    <ApplicantParameters
                      control={control}
                      watch={watch}
                      setValue={setValue}
                      errors={errors?.coApplicant}
                      applicantType={EApplicantType.CoApplicant}
                      formControl={control}
                      editDisabled={
                        editDisabled ||
                        (lockCoApplicantIncomeAndExpenses ?? false) ||
                        (isPrequalComplete && creditApplication?.coApplicant != null && !addCoApp)
                      }
                      financingProgramId={financingProgramId}
                      hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
                      isSinNeeded={isCoapplicantSinNeeded}
                    />
                  </>
                )}
              </TabPanel>
            )}
          </CardContent>

          {globalErrors.length > 0 && (
            <CardContent ref={alertRef}>
              <Alert severity="error">
                <AlertTitle>{t('common.error')}</AlertTitle>
                {globalErrors.map((error) => (
                  <Typography key={error.message}>{error.message}</Typography>
                ))}
              </Alert>
            </CardContent>
          )}

          <CardActions>
            <Grid container spacing={1} justifyContent="flex-end" size="grow">
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 3,
                }}
              >
                <AsyncActionButton
                  id="cancelNewApplicantion"
                  color="error"
                  onClick={() => {
                    if (!isEditingApplication) {
                      navigate('/credits')
                    } else {
                      navigate(`/credits/${financingProgramId}/${creditApplication.id}`)
                    }
                  }}
                >
                  {t('common.cancel')}
                </AsyncActionButton>
              </Grid>
              {creditApplication.status === ECreditApplicationStatus.Draft && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 4,
                    md: 3,
                  }}
                >
                  <AsyncActionButton
                    id="saveDraft"
                    type="submit"
                    value="draft"
                    disabled={isPending && draftMod}
                    isPending={isPending && draftMod}
                    isError={isError && draftMod}
                    onClick={() => {
                      setDraftMod(true)
                    }}
                  >
                    {t('common.saveDraft')}
                  </AsyncActionButton>
                </Grid>
              )}

              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 3,
                }}
              >
                {!isQualify && (
                  <AsyncActionButton
                    id="prequalify"
                    type="button"
                    variant="contained"
                    value="prequalify"
                    disabled={isPending && !draftMod}
                    color="primary"
                    isError={!isFormValid || globalErrors.length > 0 || (isError && !draftMod)}
                    isPending={isPending && !draftMod}
                    onClick={() => onPrequalify()}
                  >
                    {t('common.prequalify')}
                  </AsyncActionButton>
                )}

                {isQualify && (
                  <AsyncActionButton
                    id="qualify"
                    type="button"
                    variant="contained"
                    value="qualify"
                    disabled={isPending && !draftMod}
                    color="warning"
                    isError={!isFormValid || globalErrors.length > 0 || (isError && !draftMod)}
                    isPending={isPending && !draftMod}
                    onClick={() => onSubmit()}
                  >
                    {t('common.submit')}
                  </AsyncActionButton>
                )}
              </Grid>
            </Grid>
          </CardActions>
          <CreditConsentDialog
            openConsentDialog={openConsentDialog}
            setOpenConsentDialog={setOpenConsentDialog}
            consentChecked={consentChecked}
            handleCheckboxChange={handleCheckboxChange}
            handleConfirm={handleSubmit((data) => handleConfirm(data as CreditApplication), reportErrorToConsole)}
            dialogContent={getDialogContent}
            buttonText={getDialogConfirmationButtonText}
            checkBoxText={dialogRender === DialogComponentRender.submit ? t('common.yes') : undefined}
          />
        </Card>
      </form>
    </>
  )
}
export default CreditApplicationForm
